import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  openTicketCountSchema,
  supportTicketDetailSchema,
  supportTicketSchema,
  supportTicketSummarySchema,
} from './schemas';
import type {
  CreateTicketInput,
  SendMessageInput,
  SupportTicket,
  SupportTicketDetail,
  SupportTicketSummary,
  TicketTab,
} from './types';

/** Number of Open/Active tickets the grower currently holds (drives the limit gate). */
export async function getOpenTicketCount(): Promise<number> {
  const res = await apiClient.get(endpoints.support.openCount);
  return openTicketCountSchema.parse(res).count;
}

/**
 * Raise a support ticket. The backend binds the device serial, runs the open-ticket-limit
 * and duplicate guards, and returns the created ticket (with its ref) on success. Limit /
 * duplicate violations surface as `ApiError`s (codes in SUPPORT_ERROR_CODES).
 */
export async function createTicket(input: CreateTicketInput): Promise<SupportTicket> {
  return supportTicketSchema.parse(await apiClient.post(endpoints.support.raise, input));
}

/**
 * The grower's tickets for one tab (story 101970). The backend filters by the tab's
 * status group and sorts latest-replied-first; the listing reveals them in client-side
 * windows of TICKETS_PAGE_SIZE.
 */
export async function listTickets(tab: TicketTab): Promise<SupportTicketSummary[]> {
  const res = await apiClient.getList<SupportTicketSummary>(endpoints.support.tickets, {
    params: { status: tab },
  });
  return res.data.map((t) => supportTicketSummarySchema.parse(t));
}

/** Full ticket conversation for the detail drawer. */
export async function getTicketDetail(id: string): Promise<SupportTicketDetail> {
  return supportTicketDetailSchema.parse(await apiClient.get(endpoints.support.detail(id)));
}

/** Post a grower reply; the backend appends it and returns the updated conversation. */
export async function sendMessage(
  id: string,
  input: SendMessageInput,
): Promise<SupportTicketDetail> {
  return supportTicketDetailSchema.parse(
    await apiClient.post(endpoints.support.messages(id), input),
  );
}

/** Mark a ticket read — clears its unread indicator (story 101970 §3). */
export async function markTicketRead(id: string): Promise<void> {
  await apiClient.post(endpoints.support.read(id));
}
